import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCalendar, FiSearch, FiX } from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { useHistory } from "react-router-dom";
import { getAuth } from "../../services/localStorage";
import { API_ENDPOINT } from "../../services/api";
import ConfirmDialog from "../ConfirmDialog";
import Labels from "../../services/apiLabels";
import {
  authHeaders,
  errorText,
  isApiSuccess,
  listFromPayload,
  officeId,
  pick,
  readJson,
  unwrapOffice,
} from "../Offices/officeHelpers";
import { officeLabel } from "../InspectionUsers/inspectionUserHelpers";
import {
  applyAppointmentStatus,
  appointmentDate,
  appointmentId,
  appointmentTime,
  clientName,
  defaultAppointmentDateRange,
  formatAppointmentTime,
  parseAppointmentsPage,
  trackingNumber,
  vehicleReg,
  vehicleTitle,
} from "./appointmentHelpers";
import OfficeAppointmentDetail from "./OfficeAppointmentDetail";
import AppointmentStatusBadge from "./AppointmentStatusBadge";
import AppointmentActionsMenu from "./AppointmentActionsMenu";
import "../Offices/offices.css";

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const FETCH_PAGE_SIZE = 100;

function sortKey(item) {
  const date = String(appointmentDate(item) || "");
  const time = String(appointmentTime(item) || "00:00");
  return `${date}T${time}`;
}

function matchesSearch(item, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = [
    clientName(item),
    vehicleTitle(item),
    vehicleReg(item),
    trackingNumber(item),
    item?.evaluation?.variant_name,
    item?.evaluation?.brand_name,
    item?.evaluation?.model_name,
    officeLabel(item.__office),
    pick(item.__office, ["Address", "address"], ""),
  ]
    .map((part) => String(part || "").toLowerCase())
    .join(" ");
  return hay.includes(q);
}

function scrollListTop(anchorEl) {
  const body = anchorEl?.closest?.(".dd-panel__body");
  if (body) {
    body.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function AllAppointmentsList() {
  const history = useHistory();
  const auth = getAuth();
  const listTopRef = useRef(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState({ type: "", text: "" });
  const defaultRange = defaultAppointmentDateRange();
  const [startingDate, setStartingDate] = useState(defaultRange.starting_date);
  const [endingDate, setEndingDate] = useState(defaultRange.ending_date);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewing, setViewing] = useState(null);

  const loadAppointments = async () => {
    if (!auth?.token) {
      setLoading(false);
      setAppointments([]);
      return;
    }
    setLoading(true);
    try {
      const officesRes = await fetch(API_ENDPOINT(Labels.office_listing), {
        headers: authHeaders(auth.token),
      });
      const officesJson = await readJson(officesRes);
      if (!isApiSuccess(officesRes, officesJson)) {
        throw new Error("offices failed");
      }
      const offices = listFromPayload(officesJson)
        .map(unwrapOffice)
        .filter((office) => officeId(office));

      const paramsBase = new URLSearchParams({
        page: "1",
        page_size: String(FETCH_PAGE_SIZE),
      });
      if (startingDate) paramsBase.set("starting_date", startingDate);
      if (endingDate) paramsBase.set("ending_date", endingDate);

      const batches = await Promise.all(
        offices.map(async (office) => {
          const id = officeId(office);
          try {
            const response = await fetch(
              `${API_ENDPOINT(
                Labels.office_appointments_listing
              )}${id}/?${paramsBase.toString()}`,
              { headers: authHeaders(auth.token) }
            );
            const json = await readJson(response);
            if (!isApiSuccess(response, json)) return [];
            return parseAppointmentsPage(json).items.map((item) => ({
              ...item,
              __office: office,
              __officeId: id,
            }));
          } catch {
            return [];
          }
        })
      );

      const merged = batches
        .flat()
        .sort((a, b) => String(sortKey(b)).localeCompare(String(sortKey(a))));
      setAppointments(merged);
    } catch {
      setAppointments([]);
      setBanner((prev) =>
        prev.type === "success"
          ? prev
          : {
              type: "error",
              text: "Could not load appointments. Please try again.",
            }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startingDate, endingDate]);

  useEffect(() => {
    setPage(1);
  }, [startingDate, endingDate, pageSize, search]);

  const filteredList = useMemo(
    () => appointments.filter((item) => matchesSearch(item, search)),
    [appointments, search]
  );

  const totalPages = Math.max(1, Math.ceil(filteredList.length / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const pageStart = filteredList.length ? (safePage - 1) * pageSize : 0;
  const pageEnd = Math.min(pageStart + pageSize, filteredList.length);
  const pagedList = filteredList.slice(pageStart, pageEnd);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const changePage = (next) => {
    setPage(next);
    scrollListTop(listTopRef.current);
  };

  const confirmDelete = async () => {
    const id = appointmentId(deleteTarget);
    if (!id || !auth?.token) return;
    setDeleting(true);
    try {
      const response = await fetch(
        API_ENDPOINT(`${Labels.delete_office_appointment}${id}/`),
        { method: "DELETE", headers: authHeaders(auth.token) }
      );
      const json = await readJson(response);
      if (!isApiSuccess(response, json) && response.status !== 204) {
        throw new Error(errorText(json, "Could not delete this appointment."));
      }
      setDeleteTarget(null);
      setBanner({ type: "success", text: "Appointment removed." });
      await loadAppointments();
    } catch (err) {
      setBanner({
        type: "error",
        text: err?.message || "Could not delete this appointment.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusUpdated = (item, status) => {
    const id = appointmentId(item);
    setAppointments((prev) =>
      prev.map((row) =>
        String(appointmentId(row)) === String(id)
          ? applyAppointmentStatus(row, status)
          : row
      )
    );
    setViewing((current) =>
      current && String(appointmentId(current)) === String(id)
        ? applyAppointmentStatus(current, status)
        : current
    );
    setBanner({
      type: "success",
      text:
        status === "approved"
          ? "Appointment approved."
          : "Appointment rejected.",
    });
  };

  return (
    <div className="dd-section dd-offices" ref={listTopRef}>
      <div className="dd-arb-intro">
        <span className="dd-arb-intro__badge" aria-hidden>
          <FiCalendar size={18} />
        </span>
        <div>
          <h2>All appointments</h2>
          <p>Bookings across every inspection centre.</p>
        </div>
      </div>

      {banner.text ? (
        <p
          className={
            banner.type === "success"
              ? "dd-hours-alert dd-hours-alert--success"
              : "dd-hours-alert dd-hours-alert--error"
          }
          role="status"
        >
          {banner.text}
        </p>
      ) : null}

      {loading ? (
        <div className="dd-empty" aria-busy="true">
          <strong>Loading appointments…</strong>
          <p>Fetching bookings from your inspection centres.</p>
        </div>
      ) : appointments.length ? (
        <>
          <div className="dd-toolbar">
            <label className="dd-toolbar__search">
              <FiSearch size={16} aria-hidden />
              <input
                type="search"
                placeholder="Search client, vehicle, registration, office…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search appointments"
              />
              {search ? (
                <button
                  type="button"
                  className="dd-toolbar__clear-input"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <FiX size={14} />
                </button>
              ) : null}
            </label>
            <label className="dd-toolbar__field">
              <span>From</span>
              <input
                type="date"
                value={startingDate}
                max={endingDate || undefined}
                onChange={(e) => setStartingDate(e.target.value)}
              />
            </label>
            <label className="dd-toolbar__field">
              <span>To</span>
              <input
                type="date"
                value={endingDate}
                min={startingDate || undefined}
                onChange={(e) => setEndingDate(e.target.value)}
              />
            </label>
            <label className="dd-toolbar__field">
              <span>Per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="dd-toolbar__meta">
            {filteredList.length ? (
              <span>
                Showing {pageStart + 1}–{pageEnd} of {filteredList.length}
                {filteredList.length !== appointments.length
                  ? ` (filtered from ${appointments.length})`
                  : ""}
              </span>
            ) : (
              <span>No matching appointments</span>
            )}
          </div>

          {filteredList.length ? (
            <>
              <div className="dd-table-wrap">
                <table className="dd-table">
                  <thead>
                    <tr>
                      <th scope="col">Office</th>
                      <th scope="col">Client</th>
                      <th scope="col">Vehicle</th>
                      <th scope="col">Registration</th>
                      <th scope="col">Date</th>
                      <th scope="col">Time</th>
                      <th scope="col">Status</th>
                      <th scope="col">Tracking</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedList.map((item, index) => {
                      const id = appointmentId(item);
                      const oid = item.__officeId;
                      return (
                        <tr
                          key={id || `appt-${pageStart + index}`}
                          className="dd-table__click-row"
                          onClick={() => setViewing(item)}
                        >
                          <td>
                            <button
                              type="button"
                              className="dd-offices__address"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!oid) return;
                                history.push(
                                  `/dealers/dashbord/offices/${oid}/appointments`,
                                  { office: item.__office }
                                );
                              }}
                            >
                              {officeLabel(item.__office) ||
                                pick(
                                  item.__office,
                                  ["Address", "address"],
                                  "Office"
                                )}
                            </button>
                          </td>
                          <td>{clientName(item)}</td>
                          <td>
                            <strong>{vehicleTitle(item)}</strong>
                            <div className="dd-muted">
                              {item?.evaluation?.variant_name || "—"}
                            </div>
                          </td>
                          <td>{vehicleReg(item)}</td>
                          <td>{appointmentDate(item)}</td>
                          <td>
                            {formatAppointmentTime(appointmentTime(item))}
                          </td>
                          <td>
                            <AppointmentStatusBadge item={item} />
                          </td>
                          <td>{trackingNumber(item)}</td>
                          <td>
                            <AppointmentActionsMenu
                              item={item}
                              token={auth?.token}
                              onStatusUpdated={handleStatusUpdated}
                              onDelete={setDeleteTarget}
                              onError={(text) =>
                                setBanner({ type: "error", text })
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredList.length > pageSize ? (
                <div className="dd-pagination">
                  <PaginationControl
                    page={safePage}
                    between={3}
                    total={filteredList.length}
                    limit={pageSize}
                    changePage={changePage}
                    ellipsis={1}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <div className="dd-empty">
              <strong>No matches</strong>
              <p>Try clearing search or changing the date range.</p>
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={() => setSearch("")}
              >
                Clear search
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="dd-empty">
          <strong>No appointments yet</strong>
          <p>No bookings in this date range across your offices.</p>
        </div>
      )}

      <OfficeAppointmentDetail
        item={viewing}
        onClose={() => setViewing(null)}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this appointment?"
        message={
          deleteTarget
            ? `Remove ${clientName(deleteTarget)} · ${vehicleTitle(
                deleteTarget
              )} on ${appointmentDate(deleteTarget)} at ${formatAppointmentTime(
                appointmentTime(deleteTarget)
              )}? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete appointment"
        confirming={deleting}
        onCancel={() => (!deleting ? setDeleteTarget(null) : null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default AllAppointmentsList;
